import React from "react";

/**
 * Label-inside text / date inputs, select and textarea. They share the look of
 * `SearchableSelect` (filled bg-base-300, rounded-md, small muted label pinned
 * top-left, value underneath) so a form mixing selects and inputs reads as one
 * consistent set. The label is always pinned (never animates over the value),
 * so placeholders stay visible.
 */

// text-base on mobile keeps iOS Safari from zooming the page on focus.
export const floatingControlClass =
  "block w-full border rounded-md bg-base-300 text-base-content text-base sm:text-sm font-grotesk leading-5 pl-4 pr-4 pt-6 pb-2 placeholder:text-base-content/30 focus:outline-none focus:ring-0 focus:border-base-300 disabled:opacity-50";

export const floatingLabelClass =
  "absolute left-4 top-1 text-xs text-base-content/60 font-grotesk pointer-events-none";

export const floatingBorderFor = (error?: boolean): string =>
  error ? "border-error focus:border-error" : "border-base-300";

interface FloatingInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "className"> {
  id: string;
  label: React.ReactNode;
  error?: boolean;
  className?: string;
  /** Forwarded to the underlying input. */
  inputRef?: React.Ref<HTMLInputElement>;
}

export const FloatingInput: React.FC<FloatingInputProps> = ({
  id,
  label,
  error,
  className = "",
  inputRef,
  ...rest
}) => (
  <div className={`relative ${className}`}>
    <input
      id={id}
      ref={inputRef}
      aria-invalid={error || undefined}
      {...rest}
      className={`${floatingControlClass} ${floatingBorderFor(error)} font-semibold`}
    />
    <label htmlFor={id} className={floatingLabelClass}>
      {label}
    </label>
  </div>
);

interface FloatingSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "className"> {
  id: string;
  label: React.ReactNode;
  error?: boolean;
  className?: string;
}

export const FloatingSelect: React.FC<FloatingSelectProps> = ({
  id,
  label,
  error,
  className = "",
  children,
  ...rest
}) => (
  <div className={`relative ${className}`}>
    <select
      id={id}
      aria-invalid={error || undefined}
      {...rest}
      className={`${floatingControlClass} ${floatingBorderFor(error)} appearance-none pr-10 font-semibold`}
    >
      {children}
    </select>
    <label htmlFor={id} className={floatingLabelClass}>
      {label}
    </label>
    <svg
      className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/50 pointer-events-none"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  </div>
);

interface FloatingTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "className"> {
  id: string;
  label: React.ReactNode;
  error?: boolean;
  className?: string;
}

export const FloatingTextarea: React.FC<FloatingTextareaProps> = ({
  id,
  label,
  error,
  className = "",
  rows = 3,
  ...rest
}) => (
  <div className={`relative ${className}`}>
    <textarea
      id={id}
      rows={rows}
      aria-invalid={error || undefined}
      {...rest}
      className={`${floatingControlClass} ${floatingBorderFor(error)} font-normal min-h-[96px] resize-none`}
    />
    <label htmlFor={id} className={floatingLabelClass}>
      {label}
    </label>
  </div>
);
